import axios from "axios";

import { ApiConfig } from "../constant/api_constants/url";
import { API_KEY, ENABLE_CRYPTO } from "../constant/environment";
import { Messages } from "../constant/messages/toastMessages";
import { API_METHODS } from "../enums";
import { decryptData, encryptData } from "./api/encryption";
import { consoleLog } from "./consoleLog/consoleLog";
import { isValidJWT } from "./authentication/isValidJWT";
import { getUserTimezoneHeader } from "./userTimezone";

const handleError = (statusCode: number) => {
  if (!statusCode) {
    return;
  }

  let message = "";
  let title = "Error";

  switch (statusCode) {
    // Client-Side
    case 400:
      message = Messages["client.side.400"].message;
      title = Messages["client.side.400"].messageTitle;
      break;
    case 401:
      message = Messages["client.side.401"].message;
      title = Messages["client.side.401"].messageTitle;
      break;
    case 402:
      message = Messages["client.side.402"].message;
      title = Messages["client.side.402"].messageTitle;
      break;
    case 403:
      message = Messages["client.side.403"].message;
      title = Messages["client.side.403"].messageTitle;
      break;
    case 404:
      message = Messages["client.side.404"].message;
      title = Messages["client.side.404"].messageTitle;
      break;
    case 405:
      message = Messages["client.side.405"].message;
      title = Messages["client.side.405"].messageTitle;
      break;

    // Server-Side
    case 500:
      message = Messages["server.side.500"].message;
      title = Messages["server.side.500"].messageTitle;
      break;
    case 501:
      message = Messages["server.side.501"].message;
      title = Messages["server.side.501"].messageTitle;
      break;
    case 502:
      message = Messages["server.side.502"].message;
      title = Messages["server.side.502"].messageTitle;
      break;
    case 503:
      message = Messages["server.side.503"].message;
      title = Messages["server.side.503"].messageTitle;
      break;
    case 504:
      message = Messages["server.side.504"].message;
      title = Messages["server.side.504"].messageTitle;
      break;

    // Default case for unknown errors
    default:
      message = Messages["api.error"].message;
      title = Messages["api.error"].messageTitle;
  }

  console.error(`Error (${statusCode}): ${title} - ${message}`);
};

// Generic API request function
export const fetchApi = async (
  accessJWT: string,
  endpoint: string,
  method: API_METHODS.GET | API_METHODS.POST | API_METHODS.PUT | API_METHODS.DELETE,
  body?: any,
  params?: Record<string, string | number> | null,
  controller?: AbortController,
  extraHeaders?: Record<string, string>
) => {
  const url = ApiConfig.getFullUrl(endpoint, params || {});
  const isPixelEvent = endpoint === "facebook/pixelEvent";
  try {
    let response = null;
    if (endpoint === "intent/event") {
      const { userId: _userId, ...bodyForLog } = body || {};
      consoleLog(["INTENT", body?.event_name, url, method, bodyForLog]);
    } else if (!isPixelEvent) {
      consoleLog(["API REQUEST", url, method, body]);
    }
    if (body?.bypassJWTValidation !== true && accessJWT && !isValidJWT(accessJWT)) return null;
    if (accessJWT) {
      response = await axios({
        url,
        method,
        headers: {
          "Content-Type": `${ENABLE_CRYPTO === "false" ? "application/json" : "text/plain"}`,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "*",
          "x-access-token": accessJWT,
          "x-api-key": API_KEY || "",
          // Conditional — `{}` when the CORS flag is off or no usable zone.
          ...getUserTimezoneHeader(),
          ...(extraHeaders ?? {}),
        },
        data: encryptData(body),
        signal: controller?.signal,
      });
    } else {
      response = await axios({
        url,
        method,
        headers: {
          "Content-Type": "text/plain",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "*",
          "x-api-key": API_KEY || "",
          ...getUserTimezoneHeader(),
          ...(extraHeaders ?? {}),
        },
        data: encryptData(body),
        signal: controller?.signal,
      });
    }

    if (response?.status !== 200) {
      handleError(response?.status);
      return null;
    }
    const decryptedData = decryptData(response?.data);
    const decryptedResponse = decryptedData ? JSON.parse(decryptedData) : null;
    if (!isPixelEvent) {
      consoleLog(["API RESPONSE", url, response?.status, decryptedResponse]);
    }
    return decryptedResponse;
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      console.error("API call was canceled");
      return null;
    }
    console.error("Error in API call:", error);
    throw error;
  }
};
